import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  FiMonitor, 
  FiShield, 
  FiZap, 
  FiTrendingUp, 
  FiUsers, 
  FiGlobe,
  FiCheckCircle,
  FiArrowRight
} from 'react-icons/fi';
import { trackPageView, trackButtonClick } from '../utils/analytics';
import { useSEO } from '../hooks/useSEO';
import './Home.css';

const Home = () => {
  // SEO management
  useSEO({
    title: 'LogCrest - AI-Powered Observability Platform | Log Management & Infrastructure Monitoring',
    description: 'Transform your observability with LogCrest\'s AI-powered platform. Monitor logs, infrastructure, and performance with intelligent insights. Start your free trial today.',
    keywords: 'observability platform, log management, infrastructure monitoring, AI ops, SRE tools, performance monitoring, cloud monitoring, DevOps platform',
    structuredData: {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'LogCrest',
      url: 'https://www.logcrest.com',
      logo: 'https://www.logcrest.com/logo.png',
      description: 'AI-powered observability platform for modern infrastructure',
      sameAs: [
        'https://twitter.com/LogCrest',
        'https://linkedin.com/company/logcrest',
        'https://github.com/logcrest'
      ],
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+1-XXX-XXX-XXXX',
        contactType: 'customer service',
        areaServed: 'US',
        availableLanguage: 'English'
      }
    }
  });

  useEffect(() => {
    // Track page visit with comprehensive analytics
    trackPageView('/', {
      page_title: 'LogCrest - AI-Powered Observability Platform',
      page_category: 'home'
    });
  }, []);

  const features = [
    {
      icon: <FiMonitor />,
      title: 'Real-time Monitoring',
      description: 'Monitor your logs and infrastructure in real-time with intelligent alerts and comprehensive dashboards.'
    },
    {
      icon: <FiShield />,
      title: 'Advanced Security',
      description: 'Protect your infrastructure with AI-powered security insights and compliance monitoring.'
    },
    {
      icon: <FiZap />,
      title: 'AI-Powered Insights',
      description: 'Get intelligent recommendations and automated responses to optimize your infrastructure.'
    },
    {
      icon: <FiTrendingUp />,
      title: 'Performance Analytics',
      description: 'Analyze performance trends and identify bottlenecks before they impact your users.'
    },
    {
      icon: <FiUsers />,
      title: 'Team Collaboration',
      description: 'Collaborate effectively with role-based access control and shared dashboards.'
    },
    {
      icon: <FiGlobe />,
      title: 'Multi-Cloud Support',
      description: 'Monitor across AWS, GCP, Azure, and on-premise environments seamlessly.'
    }
  ];

  const stats = [
    { number: '10K+', label: 'Active Clusters' },
    { number: '99.9%', label: 'Uptime SLA' },
    { number: '50%', label: 'Cost Reduction' },
    { number: '24/7', label: 'Support' }
  ];

  const systemHealthMetrics = [
    {
      title: 'System Health',
      value: '98.7%',
      change: '+2.1%',
      icon: <FiMonitor />,
      description: 'Monitor your infrastructure health with real-time system status and automated alerts.'
    },
    {
      title: 'Active Users',
      value: '2,847',
      change: '+12.3%',
      icon: <FiUsers />,
      description: 'Track user engagement and system usage patterns with comprehensive analytics.'
    },
    {
      title: 'Revenue',
      value: '$12,450',
      change: '+15.7%',
      icon: <FiTrendingUp />,
      description: 'Monitor business metrics and revenue growth with intelligent financial insights.'
    },
    {
      title: 'Response Time',
      value: '245ms',
      change: '-18ms',
      icon: <FiTrendingUp />,
      description: 'Optimize application performance with real-time response time monitoring.'
    }
  ];


  const benefits = [
    'Reduce infrastructure costs by up to 50%',
    'Deploy in minutes, not weeks',
    'Scale with confidence',
    'Enterprise-grade security',
    '24/7 expert support'
  ];

  return (
    <div className="home">
      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <div className="hero-content">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              Transforming Observability with the AgentSRE Suite
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.1 }}
              style={{ fontSize: '1.1rem', fontWeight: '500', marginBottom: '0.5rem' }}
            >
              Powered by the Industry's Advanced Agentic Data Model
            </motion.p>
            <motion.div
              className="hero-buttons"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <Link 
                to="/pricing" 
                className="btn btn-primary"
                onClick={() => trackButtonClick('Get Started Free', 'hero_section')}
              >
                Get Started Free
                <FiArrowRight />
              </Link>
              <Link 
                to="/demo" 
                className="btn btn-secondary"
                onClick={() => trackButtonClick('Request Demo', 'hero_section')}
              >
                Request Demo
              </Link>
            </motion.div>
          </div>
        </div>
      </section>



      {/* Stats Section */}
      <section className="stats">
        <div className="container">
          <div className="stats-grid">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                className="stat-item"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
              >
                <h3>{stat.number}</h3>
                <p>{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features">
        <div className="container">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            Everything you need for observability
          </motion.h2>
          <div className="grid grid-3">
            {/* First feature card - Real-time Monitoring */}
            <motion.div
              className="feature-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0 * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="feature-icon">
                {features[0].icon}
              </div>
              <h3>{features[0].title}</h3>
              <p>{features[0].description}</p>
            </motion.div>

            {/* 4 Metric cards */}
            {systemHealthMetrics.map((metric, index) => (
              <motion.div
                key={`metric-${index}`}
                className="health-metric-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: (index + 1) * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="metric-header">
                  <div className="metric-icon">
                    {metric.icon}
                  </div>
                  <h3>{metric.title}</h3>
                </div>
                <div className="metric-value">
                  <span className="value">{metric.value}</span>
                  {metric.change && (
                    <span className={`change ${metric.change.startsWith('+') ? 'positive' : 'negative'}`}>
                      {metric.change}
                    </span>
                  )}
                </div>
                <p className="metric-description">{metric.description}</p>
              </motion.div>
            ))}

            {/* Remaining feature cards */}
            {features.slice(1).map((feature, index) => (
              <motion.div
                key={index + 1}
                className="feature-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: (index + 5) * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="feature-icon">
                  {feature.icon}
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits">
        <div className="container">
          <div className="benefits-content">
            <motion.div
              className="benefits-text"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2>Why choose LogCrest?</h2>
              <p>
                Join thousands of teams who trust LogCrest to monitor their logs and infrastructure. 
                Our AI-powered platform helps you reduce costs, improve performance, and ensure reliability.
              </p>
              <ul className="benefits-list">
                {benefits.map((benefit, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <FiCheckCircle />
                    {benefit}
                  </motion.li>
                ))}
              </ul>
              <Link to="/pricing" className="btn btn-primary">
                Contact Sales
                <FiArrowRight />
              </Link>
            </motion.div>
            <motion.div
              className="benefits-visual"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="dashboard-preview">
                <div className="dashboard-header">
                  <div className="dashboard-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <span className="dashboard-title">LogCrest Dashboard</span>
                </div>
                <div className="dashboard-content">
                  <div className="metric-card">
                    <div className="metric-value">99.9%</div>
                    <div className="metric-label">Uptime</div>
                  </div>
                  <div className="metric-card">
                    <div className="metric-value">1.2s</div>
                    <div className="metric-label">Response Time</div>
                  </div>
                  <div className="metric-card">
                    <div className="metric-value">45%</div>
                    <div className="metric-label">Cost Saved</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2>Ready to get started?</h2>
            <p>
              Join thousands of teams already using LogCrest to monitor and optimize their logs and infrastructure.
            </p>
            <div className="cta-buttons">
              <Link 
                to="/pricing" 
                className="btn btn-primary"
                onClick={() => trackButtonClick('Get Started Free', 'cta_section')}
              >
                Get Started Free
                <FiArrowRight />
              </Link>
              <Link 
                to="/demo" 
                className="btn btn-secondary"
                onClick={() => trackButtonClick('Schedule Demo', 'cta_section')}
              >
                Schedule Demo
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
